/**
 * EvaInsights — deterministic labor insight engine (v2).
 *
 * generateEvaInsights() is a pure function — no side effects, no external calls.
 * It applies 5 rules over already-loaded payroll / timesheet data and returns
 * a sorted, deduplicated list of max 3 actionable recommendations with quantified impact.
 *
 * Output schema per insight:
 *   { type, impactValue, title, action, impact, confidence, icon }
 *
 * Rules:
 *   1. High-cost employee  — individual cost/hr > avg * 1.2
 *   2. Overtime-heavy employee  — OT% > 25% of that employee's hours
 *   3. Overstaffed day  — day cost > daily avg * 1.2
 *   4. Location efficiency gap  — cost/hr difference > 15% between locations
 *   5. Overtime spike  — cost/hr rising in latter half of period (or global OT > 20%)
 *
 * Sorting: highest impactValue (dollar) first. Cap: 3.
 */

import React, { useMemo } from 'react';
import { DollarSign, Clock, TrendingDown, MapPin, CheckCircle, Lightbulb, ArrowRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';

// ── Helpers ───────────────────────────────────────────────────────────────────

const firstName = name => (name || '').split(' ')[0] || name;
const pct = (val, ref) => Math.round(((val - ref) / ref) * 100);
// Compact money formatter: $1.2k or $45
const money = v => v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`;

// Assumed OT premium factor: 1.5x rate → 0.5x extra above base
const OT_PREMIUM = 0.5;

// ── Rules engine ─────────────────────────────────────────────────────────────

/**
 * generateEvaInsights({ employees, periodTotals, dailyData, locationId })
 *
 * All inputs come from already-loaded state — no API calls, no side effects.
 * Returns: Array<{ type, impactValue, title, action, impact, confidence, icon }>
 *          sorted by impactValue DESC, capped at 3.
 */
export function generateEvaInsights({ employees = [], periodTotals = {}, dailyData = [], locationId = 'all' }) {
  const insights = [];

  const totalHours = periodTotals.total_hours ?? 0;
  const totalCost  = periodTotals.total_pay   ?? 0;
  const totalOT    = periodTotals.overtime_hours ?? 0;

  const wageEmps  = employees.filter(e => e.hourly_wage != null && (e.total_hours ?? 0) > 0);
  const hasWages  = wageEmps.length > 0;
  const avgCostPH = totalHours > 0 && totalCost > 0 ? totalCost / totalHours : 0;

  // ── RULE 1: High-cost employee (cost/hr > avg * 1.2) ─────────────────────
  if (hasWages && avgCostPH > 0) {
    wageEmps
      .map(e => ({ ...e, cph: (e.total_pay ?? 0) / e.total_hours }))
      .filter(e => e.cph > avgCostPH * 1.2)
      .sort((a, b) => b.cph - a.cph)
      .slice(0, 1)
      .forEach(e => {
        const savings = (e.cph - avgCostPH) * e.total_hours;
        insights.push({
          type: 'cost',
          impactValue: savings,
          title: 'High labor cost detected',
          action: `Reduce shifts for ${firstName(e.employee_name)} or replace with lower-cost staff where possible`,
          impact: `Est. savings: ${money(savings)} — ${firstName(e.employee_name)} costs ${pct(e.cph, avgCostPH)}% more than avg ($${e.cph.toFixed(2)}/hr vs $${avgCostPH.toFixed(2)}/hr)`,
          confidence: 'high',
          icon: 'dollar',
        });
      });
  }

  // ── RULE 2: Overtime-heavy employee (OT% > 25% of their hours) ───────────
  employees
    .filter(e => (e.total_hours ?? 0) >= 4 && (e.overtime_hours ?? 0) > 0)
    .map(e => ({ ...e, otRatio: (e.overtime_hours ?? 0) / e.total_hours }))
    .filter(e => e.otRatio > 0.25)
    .sort((a, b) => b.otRatio - a.otRatio)
    .slice(0, 1)
    .forEach(e => {
      const baseRate   = e.hourly_wage ?? 0;
      const extraCost  = (e.overtime_hours ?? 0) * baseRate * OT_PREMIUM;
      insights.push({
        type: 'overtime',
        impactValue: extraCost,
        title: 'Excess overtime usage',
        action: `Split shifts or redistribute hours for ${firstName(e.employee_name)} to avoid overtime accumulation`,
        impact: extraCost > 0
          ? `Est. OT premium: ${money(extraCost)} — ${Math.round(e.otRatio * 100)}% of ${firstName(e.employee_name)}'s hours are overtime`
          : `${Math.round(e.otRatio * 100)}% of ${firstName(e.employee_name)}'s shifts are overtime`,
        confidence: 'high',
        icon: 'clock',
      });
    });

  // ── RULE 3: Overstaffed day (day cost > daily avg * 1.2) ─────────────────
  if (dailyData.length >= 3) {
    const metric = hasWages ? 'cost' : 'hours';
    const vals   = dailyData.map(d => d[metric]).filter(v => v > 0);
    if (vals.length > 0) {
      const avg  = vals.reduce((s, v) => s + v, 0) / vals.length;
      const peak = dailyData.reduce((m, d) => d[metric] > m[metric] ? d : m, dailyData[0]);
      if (peak[metric] > avg * 1.2) {
        const savings = peak[metric] - avg;
        insights.push({
          type: 'staffing',
          impactValue: hasWages ? savings : 0,
          title: 'Overstaffed day detected',
          action: `Reduce 1 staff during low-demand hours on ${peak.label}`,
          impact: hasWages
            ? `Est. savings: ${money(savings)} on ${peak.label} (${pct(peak[metric], avg)}% above daily avg)`
            : `${savings.toFixed(1)} excess hours on ${peak.label} vs daily avg`,
          confidence: 'medium',
          icon: 'trending',
        });
      }
    }
  }

  // ── RULE 4: Location efficiency gap (cost/hr diff > 15%) ─────────────────
  if (locationId === 'all' && hasWages) {
    const locMap = {};
    employees.forEach(e => {
      if (!e.location || !(e.total_pay > 0) || !(e.total_hours > 0)) return;
      if (!locMap[e.location]) locMap[e.location] = { cost: 0, hours: 0, name: e.location };
      locMap[e.location].cost  += e.total_pay;
      locMap[e.location].hours += e.total_hours;
    });
    const locs = Object.values(locMap)
      .filter(l => l.hours >= 2)
      .map(l => ({ ...l, cph: l.cost / l.hours }));
    if (locs.length >= 2) {
      locs.sort((a, b) => a.cph - b.cph);
      const lo = locs[0], hi = locs[locs.length - 1];
      const diffPct = pct(hi.cph, lo.cph);
      if (diffPct > 15) {
        const savings = (hi.cph - lo.cph) * hi.hours;
        insights.push({
          type: 'location',
          impactValue: savings,
          title: 'Location inefficiency',
          action: `Review staffing model at ${hi.name} — costs ${diffPct}% more per hour than ${lo.name}`,
          impact: `Est. savings: ${money(savings)} if ${hi.name} aligns to avg cost`,
          confidence: 'medium',
          icon: 'map',
        });
      }
    }
  }

  // ── RULE 5: Overtime spike ────────────────────────────────────────────────
  // Primary: cost/hr rising in latter half of period (≥6 days with wages)
  // Fallback: global OT ratio > 20%
  let spikeAdded = false;
  if (dailyData.length >= 6 && hasWages) {
    const half    = Math.floor(dailyData.length / 2);
    const cphOf   = slice => {
      const valid = slice.filter(d => d.hours > 0);
      if (!valid.length) return 0;
      return valid.reduce((s, d) => s + d.cost / d.hours, 0) / valid.length;
    };
    const firstCPH  = cphOf(dailyData.slice(0, half));
    const secondCPH = cphOf(dailyData.slice(half));
    if (firstCPH > 0 && secondCPH > firstCPH * 1.15) {
      const avgHrsPerDay  = totalHours / dailyData.length;
      const extraPerDay   = (secondCPH - firstCPH) * avgHrsPerDay;
      const totalDiff     = extraPerDay * (dailyData.length - half);
      insights.push({
        type: 'spike',
        impactValue: Math.max(0, totalDiff),
        title: 'Overtime increasing',
        action: 'Adjust schedule earlier in week to prevent overtime accumulation',
        impact: `Est. excess cost: ${money(Math.abs(totalDiff))} in latter half of this period`,
        confidence: 'medium',
        icon: 'clock',
      });
      spikeAdded = true;
    }
  }
  if (!spikeAdded) {
    const globalOTRatio = totalHours > 0 ? totalOT / totalHours : 0;
    if (globalOTRatio > 0.2 && totalOT >= 2) {
      const otExtraCost = hasWages ? totalOT * avgCostPH * OT_PREMIUM : 0;
      insights.push({
        type: 'spike',
        impactValue: otExtraCost,
        title: 'Overtime increasing',
        action: 'Adjust schedule earlier in week to prevent overtime accumulation',
        impact: hasWages && otExtraCost > 0
          ? `Est. OT premium this period: ${money(otExtraCost)} (${Math.round(globalOTRatio * 100)}% OT ratio)`
          : `${Math.round(globalOTRatio * 100)}% of all hours this period are overtime`,
        confidence: 'medium',
        icon: 'clock',
      });
    }
  }

  // Deduplicate by type+title prefix, sort by impactValue DESC, cap at 3
  const seen = new Set();
  return insights
    .filter(i => {
      const key = `${i.type}:${i.title.slice(0, 20)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => b.impactValue - a.impactValue)
    .slice(0, 3);
}

// ── Icon map ──────────────────────────────────────────────────────────────────

const ICON_MAP = {
  dollar:   { Icon: DollarSign,   bg: 'bg-orange-100',  iconCls: 'text-orange-500',  border: 'border-orange-100',  cardBg: 'bg-orange-50/40'  },
  clock:    { Icon: Clock,        bg: 'bg-amber-100',   iconCls: 'text-amber-500',   border: 'border-amber-100',   cardBg: 'bg-amber-50/40'   },
  trending: { Icon: TrendingDown, bg: 'bg-red-100',     iconCls: 'text-red-500',     border: 'border-red-100',     cardBg: 'bg-red-50/40'     },
  map:      { Icon: MapPin,       bg: 'bg-violet-100',  iconCls: 'text-violet-500',  border: 'border-violet-100',  cardBg: 'bg-violet-50/40'  },
};

const CONFIDENCE_STYLE = {
  high:   'bg-emerald-50 text-emerald-600 border-emerald-100',
  medium: 'bg-slate-100 text-slate-500 border-slate-200',
};

// ── Display component ─────────────────────────────────────────────────────────

export function EvaInsights({ employees, periodTotals, dailyData, locationId }) {
  const insights = useMemo(
    () => generateEvaInsights({ employees, periodTotals, dailyData, locationId }),
    [employees, periodTotals, dailyData, locationId]
  );

  return (
    <Card className="border-slate-200" data-testid="eva-insights-card">
      <CardContent className="pt-4 pb-4 px-4">

        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-teal-100 flex items-center justify-center shrink-0">
            <Lightbulb size={14} className="text-teal-600" />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-800 leading-none">Eva Recommendations</p>
            <p className="text-xs text-slate-400 leading-none mt-0.5">Actionable insights — ranked by estimated savings</p>
          </div>
        </div>

        {/* Insights list */}
        {insights.length === 0 ? (
          <div className="flex items-center gap-2 py-2 text-sm text-emerald-700" data-testid="eva-all-clear">
            <CheckCircle size={14} className="text-emerald-500 shrink-0" />
            Your operations look efficient this period.
          </div>
        ) : (
          <ul className="space-y-2.5" data-testid="eva-insights-list">
            {insights.map((insight, i) => {
              const { Icon, bg, iconCls, border, cardBg } = ICON_MAP[insight.icon] || ICON_MAP.clock;
              const confStyle = CONFIDENCE_STYLE[insight.confidence] || CONFIDENCE_STYLE.medium;
              return (
                <li
                  key={i}
                  className={`rounded-xl border p-3 ${border} ${cardBg}`}
                  data-testid={`eva-insight-${i}`}
                  data-type={insight.type}
                >
                  {/* Title row */}
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`w-5 h-5 rounded-md ${bg} flex items-center justify-center shrink-0`}>
                      <Icon size={11} className={iconCls} />
                    </span>
                    <p className="text-xs font-semibold text-slate-800 flex-1 leading-snug">{insight.title}</p>
                    {insight.confidence && (
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${confStyle} shrink-0`}>
                        {insight.confidence}
                      </span>
                    )}
                  </div>

                  {/* Action */}
                  <div className="flex items-start gap-1.5 pl-7 mb-1.5">
                    <ArrowRight size={9} className="text-slate-400 shrink-0 mt-[3px]" />
                    <p className="text-xs text-slate-600 leading-relaxed">{insight.action}</p>
                  </div>

                  {/* Impact */}
                  <p
                    className="text-xs font-semibold text-teal-700 pl-7"
                    data-testid={`eva-insight-impact-${i}`}
                  >
                    {insight.impact}
                  </p>
                </li>
              );
            })}
          </ul>
        )}

      </CardContent>
    </Card>
  );
}
